import { useId, useState } from "react";
import { Tag, UserPlus, X } from "lucide-react";

import {
  buscarClientes,
  clienteExacto,
  CONSUMIDOR_FINAL,
  type Cliente,
  type ClienteElegido,
} from "./cliente-modelo.ts";
import { pareceNumeroDeFactura } from "./sugerencia-factura.ts";
import { Boton, cx } from "./ui.tsx";

/**
 * ¿De quién es este pago? — elegido de una lista (SPEC-029 §5, P-2).
 *
 * Tres caminos, en este orden de comodidad:
 * 1. «Consumidor Final», de un clic: es el 64 % de lo que etiqueta Lorus.
 * 2. Buscar en los clientes que ya tienen documento, por nombre o por NIT.
 * 3. Si no está, «Es un cliente nuevo»: un paso explícito, con documento
 *    opcional (D-70). Escribir sigue siendo posible, pero ya no es el camino.
 *
 * Elegido, se ve como una ficha con su documento y una X para cambiarlo.
 */
export function CampoCliente({
  clientes,
  elegido,
  alElegir,
  origen,
  deshabilitado,
}: {
  clientes: Cliente[];
  elegido: ClienteElegido | null;
  alElegir: (c: ClienteElegido | null) => void;
  /** De dónde salió el elegido, si no lo eligió la persona: «según la factura». */
  origen?: string;
  deshabilitado?: boolean;
}) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const [nuevo, setNuevo] = useState(false);
  const [documento, setDocumento] = useState("");
  const lista = useId();

  const resultados = buscarClientes(clientes, texto);
  const exacto = clienteExacto(clientes, texto);
  const puedeSerNuevo = texto.trim().length >= 3 && !exacto;

  const elegir = (c: Cliente, esNuevo = false) => {
    alElegir({ nombre: c.nombre.trim(), nit: c.nit.trim(), nuevo: esNuevo });
    setTexto("");
    setAbierto(false);
    setNuevo(false);
    setDocumento("");
  };

  if (elegido) {
    return (
      <div className="flex min-w-52 flex-1 basis-full items-center gap-2 rounded-xl border border-marca/40 bg-marca-suave/40 px-3 py-2 text-sm">
        <Tag className="size-4 shrink-0 text-marca" aria-hidden />
        <span className="min-w-0 truncate font-medium text-tinta">{elegido.nombre}</span>
        <span className="cifra shrink-0 text-xs text-tenue">
          {elegido.nit ? `· ${elegido.nit}` : "· sin documento"}
          {elegido.nuevo ? " · cliente nuevo" : origen ? ` · ${origen}` : ""}
        </span>
        <button
          type="button"
          disabled={deshabilitado}
          onClick={() => alElegir(null)}
          aria-label={`Cambiar el cliente ${elegido.nombre}`}
          className="ml-auto grid size-6 shrink-0 place-items-center rounded-md text-tenue hover:bg-superficie-2 hover:text-tinta"
        >
          <X className="size-3.5" />
        </button>
      </div>
    );
  }

  if (nuevo) {
    return (
      <div className="flex min-w-52 flex-1 basis-full flex-wrap items-center gap-2 rounded-xl border border-borde bg-superficie-2 p-2.5 text-sm">
        <p className="basis-full text-xs text-tenue">
          «{texto.trim()}» no está en tu lista. Si es un cliente nuevo, su documento ayuda a reconocerlo la
          próxima vez.
        </p>
        <input
          value={documento}
          onChange={(e) => setDocumento(e.target.value)}
          placeholder="NIT o cédula (opcional)"
          inputMode="numeric"
          className="h-9 min-w-40 flex-1 rounded-lg border border-borde bg-superficie px-3 text-sm placeholder:text-tenue-2 focus:border-marca focus:outline-none"
        />
        <Boton
          type="button"
          tamano="sm"
          variante="suave"
          icono={UserPlus}
          onClick={() => elegir({ nombre: texto, nit: documento }, true)}
        >
          Es un cliente nuevo
        </Boton>
        <Boton type="button" tamano="sm" variante="fantasma" onClick={() => setNuevo(false)}>
          Volver
        </Boton>
      </div>
    );
  }

  const opciones = resultados.length;
  return (
    // Su propia línea: el nombre de un cliente es largo y es lo que hay que leer.
    <div className="flex min-w-52 flex-1 basis-full flex-wrap items-center gap-2">
      <div className="relative min-w-52 flex-1">
        <Tag
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue-2"
          aria-hidden
        />
        <input
          value={texto}
          disabled={deshabilitado}
          onChange={(e) => {
            setTexto(e.target.value);
            setAbierto(true);
            setActivo(0);
          }}
          onFocus={() => setAbierto(true)}
          onBlur={() => setTimeout(() => setAbierto(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActivo((i) => Math.min(i + 1, Math.max(0, opciones - 1)));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActivo((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              // Enter elige, nunca envía un nombre suelto: eso era lo que dejaba
              // «CONSU» guardado como cliente.
              e.preventDefault();
              const r = resultados[activo];
              if (r) elegir(r);
              else if (exacto) elegir(exacto);
              else if (puedeSerNuevo) setNuevo(true);
            } else if (e.key === "Escape") {
              setAbierto(false);
            }
          }}
          role="combobox"
          aria-expanded={abierto && (opciones > 0 || puedeSerNuevo)}
          aria-controls={lista}
          aria-autocomplete="list"
          placeholder="¿De quién es este pago? Busca por nombre o documento"
          className="h-10 w-full rounded-xl border border-borde bg-superficie pr-3.5 pl-10 text-sm placeholder:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
        />
        {abierto && texto.trim() && (opciones > 0 || puedeSerNuevo) && (
          <ul
            id={lista}
            role="listbox"
            className="absolute top-full right-0 left-0 z-20 mt-1 max-h-72 overflow-auto rounded-xl border border-borde bg-superficie p-1 shadow-tarjeta"
          >
            {resultados.map((c, i) => (
              <li
                key={`${c.nit}-${c.nombre}`}
                role="option"
                aria-selected={i === activo}
                onMouseDown={(e) => {
                  e.preventDefault();
                  elegir(c);
                }}
                className={cx(
                  "flex cursor-pointer items-baseline justify-between gap-3 rounded-lg px-3 py-1.5 text-sm",
                  i === activo ? "bg-marca-suave text-marca-tinta" : "text-tinta hover:bg-superficie-2",
                )}
              >
                <span className="truncate">{c.nombre}</span>
                <span className="cifra shrink-0 text-xs text-tenue">{c.nit}</span>
              </li>
            ))}
            {puedeSerNuevo && (
              <li
                role="option"
                aria-selected={opciones === 0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setNuevo(true);
                  setAbierto(false);
                }}
                className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-tenue hover:bg-superficie-2"
              >
                <UserPlus className="size-3.5" aria-hidden />
                «{texto.trim()}» es un cliente nuevo…
              </li>
            )}
          </ul>
        )}
      </div>
      <Boton
        type="button"
        tamano="sm"
        variante="suave"
        disabled={deshabilitado}
        onClick={() => elegir(CONSUMIDOR_FINAL)}
      >
        Consumidor Final
      </Boton>
      {/* Avisa, no bloquea: hay clientes que de verdad se llaman con números. */}
      {pareceNumeroDeFactura(texto) && (
        <p className="basis-full text-xs text-aviso-tinta">
          «{texto.trim()}» parece un número de factura. Aquí va el cliente; la factura, en el campo de
          facturas de arriba.
        </p>
      )}
    </div>
  );
}
